import { Form, Formik, FormikProps } from 'formik';
import React from 'react';
import Button, { ButtonThemes } from 'ui-components/button/button';
import { LabelModes } from 'ui-components/molecules/labeled-inputs/labeledInput';
import SlideOutSidebar from 'ui-components/slide-out-sidebar/slideOutSidebar';
import XGSFormInput from 'ui-components/form/input/xgsFormInput';
import XGSFormSelect from 'ui-components/form/select/xgsFormSelect';
import { CreateLanePayload, createLaneValidationSchema, teamRequiredOptions, weekOptions } from 'app/data/static-load/model';
import XGSFormTextarea from 'ui-components/form/textarea/xgsFormTextarea';
import { useCreateLane } from '../hooks/useLanes';
import { useSelector } from 'react-redux';
import { staticLoadSelector } from 'slices/static-load/staticLoadSlice';
import { getDisabledDayValues, removeEmptyValues } from '../utils/utils';

interface CreateLaneSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isLoading: boolean;
}

interface CreateLaneSidebarFormValues {
  dayOfWeek: string;
  turnNumber: string;
  turnDescription: string;
  manifestType: string;
  carrierNumber: string;
  carrierName: string;
  trailerNumber: string;
  tractorNumber: string;
  driverName: string;
  driverId: string;
  routeNumber: string;
  teamRequired: string;
  notes: string;
}

const CreateLaneSidebar: React.FC<CreateLaneSidebarProps> = ({ isOpen,
  onClose }) => {

  const createLaneMutation = useCreateLane();
  const staticLoadSelectorData = useSelector(staticLoadSelector);
  const selectedDivision = staticLoadSelectorData.selectedDivision;
  const weekStartDate = staticLoadSelectorData.weekStartDate;

  const disabledTillDay = getDisabledDayValues(weekStartDate);

  return <SlideOutSidebar
    show={isOpen}
    onClose={onClose}
    header="Create Lane"
    size="small"
    className="filter-sidebar"
  >
    <Formik
      initialValues={{
        turnNumber: "",
        dayOfWeek: "",
        turnDescription: "",
        manifestType: "",
        carrierNumber: "",
        carrierName: "",
        trailerNumber: "",
        tractorNumber: "",
        driverName: "",
        driverId: "",
        routeNumber: "",
        teamRequired: "",
        notes: ""
      }}
      onSubmit={(values) => {
        const payload = removeEmptyValues({
          division: selectedDivision,
          weekStartDate: weekStartDate,
          turnNumber: values.turnNumber,
          dayOfWeek: parseInt(values.dayOfWeek),
          turnDescription: values.turnDescription,
          manifestType: values.manifestType,
          carrierNumber: values.carrierNumber,
          carrierName: values.carrierName,
          trailerNumber: values.trailerNumber,
          tractorNumber: values.tractorNumber,
          driverName: values.driverName,
          driverId: values.driverId,
          routeNumber: values.routeNumber,
          teamRequired: values.teamRequired === "Yes" ? true : false,
          notes: values.notes,
        });

        createLaneMutation.mutate(payload as CreateLanePayload);
      }}

      validationSchema={createLaneValidationSchema()}
      enableReinitialize
    >
      {(formikProps: FormikProps<CreateLaneSidebarFormValues>) => (
        <Form>
          <div className="update-lane-container">
            <div className="update-lane-grid-item">
              <XGSFormInput
                type="text"
                name="turnNumber"
                labelMode={LabelModes.column}
                label="Turn Number"
                required={true}
                className="add-edit-address-section__width-50"
              />
              <XGSFormSelect
                label="Day of Week"
                name="dayOfWeek"
                value={
                  formikProps.values.dayOfWeek
                    ? weekOptions.find((option) => option.value === formikProps.values.dayOfWeek)
                    : null
                }
                options={weekOptions.map((opt) => ({
                  ...opt,
                  isDisabled: Number(opt.value) < disabledTillDay
                }))}
                onValueChange={(option) => {
                  formikProps.setFieldValue("dayOfWeek", option?.value ?? "");
                }}
                formik
                required={true}
                className="add-edit-address-section__width-50"
                labelMode={LabelModes.column}
              />
            </div>
            <div className="update-lane-grid-item">
              <XGSFormInput
                type="text"
                name="turnDescription"
                labelMode={LabelModes.column}
                label="Turn Description"
                className="add-edit-address-section__width-50"
              />
              <XGSFormInput
                type="text"
                name="manifestType"
                labelMode={LabelModes.column}
                label="Manifest Type"
                className="add-edit-address-section__width-50"
              />
            </div>
            <div className="update-lane-grid-item">
              <XGSFormInput
                type="text"
                name="carrierNumber"
                labelMode={LabelModes.column}
                label="Carrier Number"
                className="add-edit-address-section__width-50"
              />
              <XGSFormInput
                type="text"
                name="carrierName"
                labelMode={LabelModes.column}
                label="Carrier Name"
                className="add-edit-address-section__width-50"
              />
            </div>
            <div className="update-lane-grid-item">
              <XGSFormInput
                type="text"
                name="trailerNumber"
                labelMode={LabelModes.column}
                label="Trailer Number"
                className="add-edit-address-section__width-50"
              />
              <XGSFormInput
                type="text"
                name="tractorNumber"
                labelMode={LabelModes.column}
                label="Tractor Number"
                className="add-edit-address-section__width-50"
              />
            </div>
            <div className="update-lane-grid-item">
              <XGSFormInput
                type="text"
                name="driverName"
                labelMode={LabelModes.column}
                label="Driver Name"
                className="add-edit-address-section__width-50"
              />
              <XGSFormInput
                type="text"
                name="driverId"
                labelMode={LabelModes.column}
                label="Driver ID"
                className="add-edit-address-section__width-50"
              />
            </div>
            <div className="update-lane-grid-item">
              <XGSFormInput
                type="text"
                name="routeNumber"
                labelMode={LabelModes.column}
                label="Route Number"
                className="add-edit-address-section__width-50"
              />
              <XGSFormSelect
                label="Team Required"
                name="teamRequired"
                options={teamRequiredOptions}
                value={
                  formikProps.values.teamRequired
                    ? teamRequiredOptions.find((option) => option.value === formikProps.values.teamRequired)
                    : null
                }
                onValueChange={(option) => {
                  formikProps.setFieldValue("teamRequired", option?.value ?? "");
                }}
                formik
                className="add-edit-address-section__width-50"
                labelMode={LabelModes.column}
              />
            </div>
            <div>
              <XGSFormTextarea
                name="notes"
                label="Notes"
                required={false}
              />
            </div>
            <div style={{ marginTop: "10px" }} />
            <div className="copy-move-lane-actions">
              <Button type="button" theme={ButtonThemes.gray} onClick={onClose}>Cancel</Button>
              <Button type="submit" theme={ButtonThemes.blue} disabled={!formikProps.dirty} spinner={createLaneMutation.isLoading} >Confirm</Button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  </SlideOutSidebar >;
};

export default CreateLaneSidebar;
